import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, AlertCircle, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "@/hooks/useToast";
import { useI18n } from '@/hooks/useI18n';
import { supabase } from "@/integrations/supabase/client";
import Loader from "@/components/ui/loader";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });
  const { t } = useI18n();

  useEffect(() => {
    // For Supabase password reset, the user is automatically authenticated when they click the link
    // We just need to check if we have a valid session
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          setError(t('reset.sessionCheckError'));
          return;
        }
        
        if (!session) {
          setError(t('reset.linkExpired'));
          return;
        }
        
        // Check if this is a recovery session
        if (session.user.aud !== 'authenticated') {
          setError(t('reset.invalidResetLink'));
          return;
        }
      } catch (err) {
        setError(t('reset.verificationError'));
      }
    };

    checkSession();
  }, []);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!passwordData.password || !passwordData.confirmPassword) {
      setError(t('reset.enterBoth'));
      return;
    }

    if (passwordData.password.length < 6) {
      setError(t('reset.passwordMinLength'));
      return;
    }

    if (passwordData.password !== passwordData.confirmPassword) {
      setError(t('reset.passwordsMismatch'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Update password using Supabase
      const { error } = await supabase.auth.updateUser({
        password: passwordData.password
      });

      if (error) {
        let errorMessage = t('reset.updateError');
        
        if (error.message.includes("Password should be at least")) {
          errorMessage = t('reset.passwordMinLength');
        } else if (error.message.includes("Invalid token")) {
          errorMessage = t('reset.linkExpired');
        } else if (error.message.includes("User not found")) {
          errorMessage = t('reset.userNotFound');
        }
        
        setError(errorMessage);
      } else {
        setIsSuccess(true);
        toast({
          title: t('reset.updatedTitle'),
          description: t('reset.updatedDesc'),
        });
        
        // Sign out the user after password change for security
        await supabase.auth.signOut();
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err) {
      setError(t('reset.unexpected'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#030A14] via-[#0A1525] to-[#142536] flex items-center justify-center p-4">
        <Card className="border border-gray-200 rounded-2xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              {t('reset.updatedTitle')}
            </CardTitle>
            <CardDescription className="text-gray-600">
              {t('reset.updatedDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button 
              onClick={() => navigate('/login')}
              className="w-full bg-[#030A14] hover:bg-[#030A14]/90"
            >
              {t('reset.goToLogin')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#030A14] via-[#0A1525] to-[#142536] flex items-center justify-center p-4">
              <Card className="border border-gray-200 rounded-2xl">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-blue-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">
            {t('reset.title')}
          </CardTitle>
          <CardDescription className="text-gray-600">
            {t('reset.desc')}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {error && (
            <Alert className="mb-6 border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                {t('reset.newPasswordLabel')}
              </Label>
              <div className="relative">
                <Input 
                  id="password" 
                  type={showPassword ? "text" : "password"}
                  value={passwordData.password}
                  onChange={(e) => setPasswordData({...passwordData, password: e.target.value})}
                  className="h-12 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14] pr-10"
                  placeholder="••••••••"
                  required 
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                {t('reset.confirmPasswordLabel')}
              </Label>
              <div className="relative">
                <Input 
                  id="confirmPassword" 
                  type={showConfirmPassword ? "text" : "password"}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                  className="h-12 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14] pr-10"
                  placeholder="••••••••"
                  required 
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={isLoading}
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="text-xs text-gray-500">
              <p>• {t('reset.passwordRequirementsBullet1')}</p>
              <p>• {t('reset.passwordRequirementsBullet2')}</p>
            </div>
            
            <Button 
              type="submit" 
              className="w-full h-12 bg-[#030A14] hover:bg-[#030A14]/90 text-white font-medium transition-colors duration-200"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader size="sm" className="mr-3" />
                  {t('reset.updating')}
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5 mr-2" />
                  {t('reset.changePassword')}
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Button 
              onClick={() => navigate('/login')}
              variant="ghost"
              className="text-gray-600 hover:text-gray-800"
            >
              Torna al Login
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword; 